import { CommonModule, Location } from '@angular/common';
import { Component, inject, input, OnInit, TemplateRef } from '@angular/core';
import { onMessage } from 'firebase/messaging';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuItem, MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import {
  MenuNavigationService,
  StrutturaUser,
  UserProvider,
} from '@siat-fe/auth';
import {
  FIREBASE_MESSAGING,
  UpdateService,
  USER_PERMISSIONS,
} from '@siat-fe/core';
import {
  CustomModalOptions,
  getFasciaOraria,
  ModalType,
  RadioButton,
  RadioFieldComponent,
  SmartModalAction,
  SmartModalService,
} from '@siat-fe/shared';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';
import { AppBarService } from '../../services/app-bar.service';
import { SupportoDialogComponent } from '../supporto-dialog/supporto-dialog.component';

@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    ReactiveFormsModule,
    TranslatePipe,
    RadioFieldComponent,
    MatDivider,
    MatTooltipModule,
    MatMenuItem,
  ],

  selector: 'siat-app-bar',
  templateUrl: './app-bar.component.html',
  styleUrl: './app-bar.component.scss',
})
export class AppBarComponent implements OnInit {
  /**
   * The injections.
   */
  readonly userProvider = inject(UserProvider);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly appbarService = inject(AppBarService);
  private readonly smartModalService = inject(SmartModalService);
  private readonly menuNavigationService = inject(MenuNavigationService);
  private readonly swUpdateService = inject(UpdateService);
  private messaging = inject(FIREBASE_MESSAGING);
  private readonly dialog = inject(MatDialog);
  /**
   * It streams the current app menu items.
   */
  currentAppMenuItems$ = this.menuNavigationService.currentAppMenuItems$;

  /**
   * It streams all available apps.
   */
  availableAppIds$ = this.menuNavigationService.availableAppIds$;

  /**
   * It streams the current title.
   */
  title$ = this.appbarService.pageTitle$;

  /**
   * It streams the flag to show/hide the back button.
   */
  showBackButton$ = this.appbarService.showBackButton$;

  /**
   * It streams the url to go back.
   */
  onClickBackToUrl$ = this.appbarService.onClickBackGoTo$;

  /**
   * It streams the current template.
   */
  template$: Observable<TemplateRef<null>> = this.appbarService.pageTemplate$$;

  /**
   * The current app ID
   */
  currentAppId: string | null = null;

  /**
   * It streams the current selected struttura.
   */
  userLogged$ = this.userProvider.currentUser$;
  selectedStruttura$ = this.userProvider.selectedStruttura$;
  notificationsNum$ = this.userProvider.notifications$;

  /**
   * It streams the opening hours of the selected struttura, null when it has no valid ones.
   */
  fasciaOrariaStruttura$ = this.selectedStruttura$.pipe(
    map((struttura) =>
      getFasciaOraria(struttura?.oraInizio, struttura?.oraFine),
    ),
  );

  /**
   * A boolean it tells if we are in the welcome page
   */
  areWelcomePage = input<boolean>(false);

  /**
   * A boolean it tells if we are in the struttura selection page
   */
  showStruttureList = false;

  /**
   * The available strutture for the radio component.
   */
  struttureOptItems: RadioButton[] = [];

  /**
   * The formgroup for struttura selection.
   */
  strutturaForm: FormGroup = new FormGroup({
    selectedStruttura: new FormControl(null, [Validators.required]),
  });

  constructor() {
    if (
      !this.userProvider.isAdminUser ||
      this.userProvider.userStruttureList?.length > 0
    ) {
      onMessage(this.messaging, _payload => {
        this.notificationsNum$ = this.userProvider.notifications$;
      });
    }
  }
  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.menuNavigationService.selectedAppId$
      .pipe(take(1), untilDestroyed(this))
      .subscribe(id => (this.currentAppId = id));

    this.initStrutture();
    this.handleStrutturaChange();
  }
  /**
   * Initializes the structure list and sets the initial form value.
   */
  initStrutture(): void {
    const list = this.userProvider.userStruttureList || [];
    this.struttureOptItems = list.map(s => ({ id: s?.id, name: s?.nome }));
    const current = this.userProvider.currentStruttura;
    const matchedOption = this.getRadioOption(opt => opt?.id === current?.id);
    this.selectedStrutturaCtrl?.setValue(matchedOption?.id, {
      emitEvent: false,
    });
  }

  /**
   * Listens for changes in the structure radio selection.
   */
  handleStrutturaChange(): void {
    this.strutturaForm
      .get('selectedStruttura')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        filter(newId => newId !== this.userProvider.currentStruttura?.id),
        tap(newId => {
          this.confirmStrutturaChange(newId);
        })
      )
      .subscribe();
  }

  /**
   * Opens a confirmation modal before applying structure change.
   */
  confirmStrutturaChange(newStrutturaId: string): void {
    const nomeStruttura = this.getRadioOptionById(newStrutturaId)?.name || '';
    const modalOptions = {
      title: 'Conferma cambio Struttura',
      message: `Confermi di passare a ${nomeStruttura}?`,
      config: { disableClose: true },
    } as CustomModalOptions;

    this.smartModalService
      .openSmartModal(ModalType.CONFIRM, modalOptions)
      .pipe(take(1))
      .subscribe((action: SmartModalAction) => {
        if (action === SmartModalAction.CONFIRMED) {
          const nuovaStruttura = this.getStrutturaById(newStrutturaId);
          if (nuovaStruttura) {
            this.applyStrutturaChange(nuovaStruttura);
          }
        } else {
          this.strutturaForm
            .get('selectedStruttura')
            ?.setValue(this.userProvider.currentStruttura?.id, {
              emitEvent: false,
            });
        }
      });
  }

  /**
   * Updates state and handles navigation based on user permissions.
   */
  private applyStrutturaChange(struttura: StrutturaUser): void {
    this.userProvider.setSelectedStruttura(struttura);

    const permessi = struttura?.permessiVisivi || [];
    this.userProvider.setPermissionList(permessi);
    const targetUrl = permessi.includes(USER_PERMISSIONS.coordinatore)
      ? '/home/dashboard'
      : permessi.includes(USER_PERMISSIONS.operativo)
        ? '/home/calendario'
        : '/home/dashboard';

    window.location.href = targetUrl;
  }

  /**
   * Logout the user.
   */
  logout(): void {
    const modalOptions = {
      title: 'Attenzione',
      message: 'Sei sicuro di voler uscire?',
      config: { disableClose: true },
    } as CustomModalOptions;

    this.smartModalService
      .openSmartModal(ModalType.CONFIRM, modalOptions)
      .pipe(
        take(1),
        filter(action => action === SmartModalAction.CONFIRMED),
        switchMap(() => this.router.navigate(['/logout']))
      )
      .subscribe();
  }

  /**
   * after confirmation, navigates to the app picker, skipping its single-permission auto-navigation.
   */
  goToAppsPage(): void {
    const modalOptions = {
      title: 'Attenzione',
      message: 'Sei sicuro di voler lasciare la pagina?',
    } as CustomModalOptions;

    this.smartModalService
      .openSmartModal(ModalType.CONFIRM, modalOptions)
      .pipe(
        filter(action => action === SmartModalAction.CONFIRMED),
        tap(() =>
          this.router.navigate(['/apps'], {
            state: { skipAutoNavigate: true },
          })
        )
      )
      .subscribe();
  }

  /**
   * @returns true if items is active
   */
  isItemActive(id: string | null, app: string): boolean {
    return id === app;
  }

  /**
   * Navigates to the previous page.
   */
  onClickGoBackButton(url?: string): void {
    if (url) {
      this.router.navigateByUrl(url);
      return;
    }

    console.warn('Attenzione: la pagina precedente non è stata impostata');
    this.location.back();
  }

  /**
   * shows the struttura selection section in the menu
   */
  showStrutturaSelection(): void {
    this.showStruttureList = !this.showStruttureList;
  }

  /**
   * @returns { FormControl<string> }
   */
  get selectedStrutturaCtrl(): FormControl {
    return this.strutturaForm.get('selectedStruttura') as FormControl;
  }

  /**
   * Helper: Finds a radio option by predicate.
   */
  private getRadioOption(
    predicate: (item: RadioButton) => boolean
  ): RadioButton | undefined {
    return this.struttureOptItems.find(predicate);
  }

  /**
   * Helper: Finds a full structure object from the provider list by ID.
   */
  private getStrutturaById(id: string | number): StrutturaUser | undefined {
    return this.userProvider.userStruttureList.find(s => s.id === id);
  }

  /**
   * Helper: Finds a radio option by ID.
   */
  private getRadioOptionById(id: string | number): RadioButton | undefined {
    return this.struttureOptItems.find(item => item.id === id);
  }

  /**
   * Generates the avatar string from name and surname.
   */
  getAvatar(name: string, cognome: string): string {
    return (
      (name?.charAt(0) || '').toUpperCase() +
      (cognome?.charAt(0) || '').toUpperCase()
    );
  }

  /**
   * Resetta la visibilità della selezione strutture.
   * Chiamato automaticamente alla chiusura del mat-menu.
   */
  resetStrutturaSelection(): void {
    this.showStruttureList = false;
  }

  /**
   * Check for updates.
   */
  checkUpdate(): void {
    this.swUpdateService.checkForUpdateManual();
  }

  /**
   * Opens the support request dialog.
   */
  openSupporto(): void {
    this.dialog.open(SupportoDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      autoFocus: false,
      disableClose: true,
    });
  }
}
